import React, { useCallback, useEffect, useState } from 'react'
import { Button, Col, Modal, Row } from 'react-bootstrap';
import Select from 'react-select';
import useShowBooking from '../../../../services/booking/hooks/useShowBooking';
import FormSpinner from '../../../../shared/components/spinner/FormSpinner';
import FormError from '../../../../shared/components/errors/FormError';
import { formatPrice } from '../../../../shared/utils/formatPrice';
import useCheckAvi from '../../../../services/booking/hooks/useCheckAvi';
import { RoomTypeData } from '../../../../services/booking/response/CheckAviResponse';
import useShowRate from '../../../../services/booking/hooks/useShowRate';
import { ShowRateResponseType } from '../../../../services/booking/response/ShowRateResponse';
import CurrencyInput from '../../../../shared/components/form/CurrencyInput';
import { toast } from 'sonner';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { rateSchema, RateType } from '../../../../services/booking/schema/UpdateRoomRateSchema';
import { useUpdateRoomRate } from '../../../../services/booking/hooks/useUpdateRoomRate';
import { useQueryClient } from '@tanstack/react-query';

interface MoveReservationContentProps {
    show: boolean,
    onClose: () => void,
    bookingId: string,
    roomTypeTargetId: string | null,
    roomNumberTargetId: string | null,
    onCanceled: () => void
}

const MoveReservationContent: React.FC<MoveReservationContentProps> = ({ show, onClose, bookingId, roomTypeTargetId, roomNumberTargetId, onCanceled }) => {
    const { data: booking, isError, isSuccess, isFetching } = useShowBooking({ bookingId: bookingId ?? '' });
    const { data: room, isError: isRoomError, isSuccess: isRoomSuccess, isFetching: isRoomFetching } = useCheckAvi({
        checkin_date: booking?.data?.checkin_date ?? '',
        checkout_date: booking?.data?.checkout_date ?? '',
        booking_id: bookingId ?? '',
    })
    const [newRoomType, setNewRoomType] = useState<RoomTypeData | null>(null);
    const [sameAsOriginal, setSameAsOriginal] = useState<boolean>(false);

    // Booking asal belum punya rate_plan_id (mis. hasil sync Channex yang belum
    // di-mapping) -- jalur normal (checkbox "Same as Original" bisa dilepas,
    // harga live rate plan muncul) TIDAK TERSENTUH sama sekali oleh percabangan
    // ini. Untuk mode unmapped: user WAJIB pilih rate plan baru dari dropdown
    // (dipakai HANYA untuk menentukan nilai `rate_plan_id` yang dikirim), tapi
    // harga yang dikirim ke BE tetap harga booking asli (booking.data.rates),
    // bukan hasil kalkulasi live dari rate plan yang dipilih.
    const isUnmapped = !booking?.data?.rate_plan_id;

    useEffect(() => {
        if (show) {
            setSameAsOriginal(true);
        }
    }, [show])
    const [selectedRatePlan, setSelectedRatePlan] = useState<string | null>(null);

    useEffect(() => {
        if (room) {
            const findRoom = room.data.find((item) => item.id === roomTypeTargetId);
            if (findRoom) {
                setNewRoomType(findRoom);
                if (findRoom.rate_plans && findRoom.rate_plans.length > 0) {
                    setSelectedRatePlan(findRoom.rate_plans[0].id);
                }
            }
        }
    }, [room])

    const [ratePlanOptions, setRatePlanOptions] = React.useState<{ value: string, label: string }[]>([]);

    useEffect(() => {
        if (newRoomType) {
            setRatePlanOptions(newRoomType.rate_plans?.map((ratePlan) => ({ value: ratePlan.id, label: ratePlan.name })) || []);
        }
    }, [newRoomType])

    const { data: ratePlan, isError: isRatePlanError, isSuccess: isRatePlanSuccess, isFetching: isRatePlanFetching } = useShowRate({
        checkin_date: booking?.data?.checkin_date ?? '',
        checkout_date: booking?.data?.checkout_date ?? '',
        rate_plan_id: selectedRatePlan ?? '',
    })

    const [rateDetails, setRateDetails] = useState<ShowRateResponseType>();

    const { register, handleSubmit, control, formState: { errors }, setError, reset } = useForm<RateType>({
        resolver: zodResolver(rateSchema),
    });

    // Satu-satunya tempat yang mengisi `rates` di form -- sengaja dibuat bergantung pada
    // `sameAsOriginal` di deps array, supaya SETIAP kali dialog dibuka ulang (move ke-2,
    // ke-3, dst.) atau checkbox di-toggle, sumber datanya dievaluasi ulang dari awal:
    // - "Same as Original" dicentang  -> pakai rate booking SAAT INI (booking.data.rates),
    //   bukan hasil kalkulasi live rate plan.
    // - Tidak dicentang                -> pakai harga live dari rate plan yang dipilih.
    // Sebelumnya ada efek terpisah (PriceView) yang mencoba "menimpa balik" ke rate asli,
    // tapi dependency-nya cuma nilai rate (primitive) -- kalau nilainya sama seperti move
    // sebelumnya, React skip efek itu dan harga live yang justru ke-submit walau checkbox
    // tercentang. Menyatukan jadi satu efek di sini menghilangkan race tersebut.
    useEffect(() => {
        if (!ratePlan) return;
        setRateDetails(ratePlan);

        // Mode unmapped SELALU pakai harga booking asli, apapun rate plan yang
        // dipilih user di dropdown -- dropdown itu cuma menentukan `rate_plan_id`
        // yang dikirim, bukan sumber harga.
        const rates = (isUnmapped || sameAsOriginal)
            ? (booking?.data?.rates ?? []).map((rate) => ({
                rate: Number(rate.rate),
                date: rate.date,
            }))
            : ratePlan.data.rates.map((rate) => ({
                rate: Number(rate.rate),
                date: rate.date,
            }));

        reset({
            room_type_id: newRoomType?.id,
            room_id: roomNumberTargetId ?? undefined,
            // rate_plan_id HANYA dikirim untuk mode unmapped -- payload mode normal
            // harus identik dengan sebelum perubahan ini (Zero Breaking Changes).
            rate_plan_id: isUnmapped ? (selectedRatePlan ?? undefined) : undefined,
            rates,
        });
    }, [ratePlan, sameAsOriginal, booking, newRoomType, roomNumberTargetId, reset, isUnmapped, selectedRatePlan])

    const handleClose = () => {
        onCanceled();
    };

    const handleSuccess = () => {
        reset();
        onClose();
    };

    const { mutate: moveReservation, isPending: isMovePending } = useUpdateRoomRate();

    const queryClient = useQueryClient();

    const onSubmit = useCallback((data: RateType) => {
        toast.loading('Moving reservation...', { id: 'move-reservation' });

        if (!bookingId) {
            toast.error('Booking ID is required', { id: 'move-reservation' });
            return;
        }

        if (isRatePlanError) {
            toast.error('Cannot move reservation', { id: 'move-reservation' });
            return;
        }

        moveReservation({
            bookingId: bookingId,
            formData: data,
        }, {
            onSuccess: async () => {
                handleSuccess();

                await queryClient.refetchQueries({
                    predicate: (query) => query.queryKey.some((key) => ['bookings'].includes(key as string))
                });

                toast.success('Reservation moved successfully', { id: 'move-reservation' });
            },
            onError: (mutationError) => {
                if ((mutationError as any)?.status === 422) {
                    const validationErrors = (mutationError as any)?.response?.data?.errors;
                    if (validationErrors) {
                        for (const [field, messages] of Object.entries(validationErrors)) {
                            setError(field as keyof RateType, {
                                type: "manual",
                                message: (messages as string[])[0],
                            });
                        }
                        toast.error('Failed to move reservation', { id: 'move-reservation' });
                    }
                } else {
                    toast.error(mutationError?.message || 'Failed to move reservation', { id: 'move-reservation' });
                }
            }
        });
    }, [moveReservation])

    return (
        <Modal show={show} onHide={handleClose} centered size='xl'>
            <Modal.Header closeButton>
                <Modal.Title>Move Reservation</Modal.Title>
            </Modal.Header>
            {isRatePlanSuccess && !isRatePlanFetching ? (
                <>
                    <Modal.Body>
                        {isError && (
                            <div style={{ height: '400px', overflowY: 'auto' }}>
                                <FormError />
                            </div>
                        )}
                        {isFetching && (
                            <div style={{ height: '400px', overflowY: 'auto' }}>
                                <FormSpinner />
                            </div>
                        )}
                        {isSuccess && !isFetching && (
                            <Row>
                                {/* FROM Section */}
                                <Col md={6} style={{ backgroundColor: '#F9F9F9' }} className='p-3'>
                                    <h5 className='fs-1'>From</h5>
                                    <div className='mb-3 d-flex justify-content-between'>
                                        <span>Original Room Type</span>
                                        <span className='fw-bold'>{booking.data?.room_type?.name ?? 'Unmapped'}</span>
                                    </div>
                                    <div className='mb-3 d-flex justify-content-between'>
                                        <span>Original Room Number</span>
                                        <span className='fw-bold'>{booking.data?.room?.room_number ?? 'Unmapped'}</span>
                                    </div>
                                    <div className='mb-3 d-flex justify-content-between'>
                                        <span>Original Check-in</span>
                                        <span className='fw-bold'>{booking.data?.checkin_date}</span>
                                    </div>
                                    <div className='mb-3 d-flex justify-content-between'>
                                        <span>Original Check-out</span>
                                        <span className='fw-bold'>{booking.data?.checkout_date}</span>
                                    </div>
                                    <div className='mb-3 d-flex justify-content-between'>
                                        <span>Original Rateplan</span>
                                        <span className='fw-bold'>{booking.data?.rate_plan?.name ?? 'Unmapped'}</span>
                                    </div>
                                    <div className='mb-3 d-flex justify-content-between'>
                                        <span>Room Charges</span>
                                    </div>
                                    {booking.data?.rates.length > 0 && booking.data?.rates.map((rate, index) => (
                                        <div key={index} className='mb-3 d-flex justify-content-between'>
                                            <span>{rate.date}</span>
                                            <span className='fw-bold'>{formatPrice(Number(rate.rate))}</span>
                                        </div>
                                    ))}
                                    <div className='mb-3 d-flex justify-content-between'>
                                        <span>Original Room Total</span>
                                        <span className='fw-bold'>{formatPrice(Number(booking.data?.total_rate))}</span>
                                    </div>
                                </Col>
                                {/* TO Section */}
                                {isRoomError && (
                                    <Col md={6} style={{ backgroundColor: '#F0EEEF' }} className='p-3'>
                                        <FormError />
                                    </Col>
                                )}
                                {isRoomFetching && (
                                    <Col md={6} style={{ backgroundColor: '#F0EEEF' }} className='p-3'>
                                        <FormSpinner />
                                    </Col>
                                )}
                                {isRoomSuccess && !isRoomFetching && newRoomType && (
                                    <Col md={6} style={{ backgroundColor: '#F0EEEF' }} className='p-3'>
                                        <h5 className='fs-1'>To</h5>
                                        <form onSubmit={handleSubmit(onSubmit)}>
                                            <div className='row mb-3'>
                                                <label htmlFor='newRoomType' className='col-6'>New Room Type</label>
                                                <div className='col-6'>
                                                    <span id='newRoomType'>{newRoomType.name}</span>
                                                    <input type='hidden' {...register('room_type_id')} value={newRoomType.id} />
                                                    {errors.room_type_id && <span className='text-danger'>{errors.room_type_id.message}</span>}
                                                </div>
                                            </div>
                                            <div className='row mb-3'>
                                                <label htmlFor='newRoomNumber' className='col-6'>New Room Number</label>
                                                <div className='col-6'>
                                                    <span id='newRoomNumber'>{newRoomType.rooms?.find((room) => room.id === roomNumberTargetId)?.room_number ?? 'unavailable'}</span>
                                                    <input type='hidden' {...register('room_id')} value={roomNumberTargetId ?? ''} />
                                                    {errors.room_id && <span className='text-danger'>{errors.room_id.message}</span>}
                                                </div>
                                            </div>
                                            <div className='row mb-3'>
                                                <label htmlFor='newCheckIn' className='col-6'>New Check-in</label>
                                                <div className='col-6'>
                                                    <span id='newCheckIn'>{booking.data?.checkin_date}</span>
                                                </div>
                                            </div>
                                            <div className='row mb-3'>
                                                <label htmlFor='newCheckOut' className='col-6'>New Check-out</label>
                                                <div className='col-6'>
                                                    <span id='newCheckOut'>{booking.data?.checkout_date}</span>
                                                </div>
                                            </div>
                                            <div className='row mb-3'>
                                                <label htmlFor='newRateplan' className='col-6'>New Rateplan</label>
                                                <div className='col-6'>
                                                    {(isUnmapped || sameAsOriginal === false) && (
                                                        <Select
                                                            id='newRateplan'
                                                            className='py-1 rounded-0'
                                                            value={ratePlanOptions.find((option) => option.value === selectedRatePlan)}
                                                            options={ratePlanOptions}
                                                            isDisabled={!newRoomType || (!isUnmapped && sameAsOriginal)}
                                                            onChange={(selectedOption) => {
                                                                setSelectedRatePlan(selectedOption?.value ?? null);
                                                            }}
                                                        />
                                                    )}
                                                    {!isUnmapped && sameAsOriginal === true && (
                                                        <span id='newRateplan' className='form-control py-1'>{booking.data?.rate_plan?.name}</span>
                                                    )}
                                                </div>
                                            </div>
                                            <div className='row mb-3'>
                                                <label htmlFor='roomCharges' className='col-6'>Room Charges</label>
                                                <div className='col-6'>
                                                    {isUnmapped ? (
                                                        <span className='badge bg-secondary-subtle text-dark py-2 px-2'>Rate follows original booking (unchanged)</span>
                                                    ) : rateDetails && (
                                                        <>
                                                            <input id='sameAsOriginal' type='checkbox' checked={sameAsOriginal} className='form-checkbox me-2' onChange={(e) => {
                                                                setSameAsOriginal(e.target.checked);
                                                            }} />
                                                            <label htmlFor='sameAsOriginal'>Same as Original</label>
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                            {(isUnmapped || sameAsOriginal === true) && booking.data.rates && booking.data.rates.map((charge, index) => (
                                                <div key={index} className='row mb-3'>
                                                    <label htmlFor='roomCharges' className='col-6'>{charge.date}</label>
                                                    <div className='col-6'>
                                                        {/* Display saja -- nilai sesungguhnya sudah disinkronkan oleh
                                                            efek reset() di atas (satu-satunya sumber kebenaran). */}
                                                        <Controller
                                                            name={`rates.${index}.rate`}
                                                            control={control}
                                                            render={({ field }) => (
                                                                <input
                                                                    type='text'
                                                                    value={formatPrice(Number(field.value) || 0)}
                                                                    readOnly
                                                                    disabled
                                                                    className='form-control py-1'
                                                                />
                                                            )}
                                                        />
                                                    </div>
                                                </div>
                                            ))}

                                            {!isUnmapped && sameAsOriginal === false && rateDetails && rateDetails.data.rates.map((charge, index) => (
                                                <div key={index} className='row mb-3'>
                                                    <label htmlFor='roomCharges' className='col-6'>{charge.date}</label>
                                                    <div className='col-6'>
                                                        <Controller
                                                            name={`rates.${index}.rate`}
                                                            control={control}
                                                            render={({ field }) => (
                                                                <CurrencyInput
                                                                    {...field}
                                                                    name={`rates[${index}].rate`}
                                                                    value={field.value}
                                                                    className='form-control py-1'
                                                                    onChange={(_e, rawValue) => {
                                                                        field.onChange(rawValue);
                                                                    }}
                                                                />
                                                            )}
                                                        />
                                                        {errors.rates && errors.rates[index] && <span className='text-danger'>{errors.rates[index].rate?.message}</span>}
                                                    </div>
                                                </div>
                                            ))}
                                            <div className='d-flex justify-content-between'>
                                                <span>New Room Total:</span>
                                                {(isUnmapped || sameAsOriginal === true) && (
                                                    <span className='fw-bold'>{formatPrice(Number(booking.data?.total_rate))}</span>
                                                )}
                                                {!isUnmapped && sameAsOriginal === false && rateDetails && (
                                                    <span className='fw-bold'>{formatPrice(rateDetails.data.total_rate)}</span>
                                                )}
                                            </div>
                                        </form>
                                    </Col>
                                )}
                            </Row>
                        )}
                    </Modal.Body>
                </>
            ) : isRatePlanError ? (
                <Modal.Body className="text-center">
                    <div className="d-flex flex-column align-items-center">
                        <div className="fs-1 text-danger">❌</div>
                        <p className="mt-3">
                            <strong>Move reservation cannot proceed!</strong>
                            <br />
                            The room you selected is restricted. Please choose another room.
                        </p>
                    </div>
                </Modal.Body>
            ) : (
                <Modal.Body>
                    <FormSpinner />
                </Modal.Body>
            )}
            <Modal.Footer>
                <Button variant="secondary" onClick={handleClose}>
                    Cancel
                </Button>
                <Button type='submit' variant="success" onClick={handleSubmit(onSubmit)} disabled={isMovePending || isRatePlanError || (isUnmapped && !selectedRatePlan)}>Move Reservation</Button>
            </Modal.Footer>
        </Modal>
    )
}

export default MoveReservationContent